import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { inquiryApi, agentApi, hasCapability } from "@/services/api";
import { formatError } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { Inbox, Send, Loader2, MessageSquare, Globe, Mail, UserPlus, PlusCircle, AlertCircle, CheckCircle2, ChevronRight, Lock } from "lucide-react";
import { INBOX } from "@/constants/testIds";
import { toast } from "sonner";

const CHANNEL_ICON = { WEB: Globe, EMAIL: Mail, MANUAL: UserPlus };
const CHANNEL_COLOR = { WEB: "text-sky-400", EMAIL: "text-indigo-400", MANUAL: "text-[#2E844A]" };

const STATUS_PILL = {
  NEW: "text-[#8C4B00] bg-amber-400/10 border-amber-400/20",
  PROCESSED: "text-[#2E844A] bg-emerald-400/10 border-emerald-400/20",
  FAILED: "text-rose-400 bg-rose-400/10 border-rose-400/20",
};

export default function InboxPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Authoritative capability check — backend enforces the same rule
  const canProcess = hasCapability(user, "crm.lead.create");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await inquiryApi.list({ limit: 100 });
      setInquiries(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleProcess = async (inquiry) => {
    if (inquiry.lead_id) {
      navigate(`/engage/leads/${inquiry.lead_id}`);
      return;
    }
    setProcessing(p => ({ ...p, [inquiry.id]: true }));
    try {
      const { data } = await agentApi.processInquiry(inquiry.id);
      toast.success(`Inquiry processed → Lead ${data.lead_id ? "created" : "linked"}`);
      await load();
      if (data.lead_id) navigate(`/engage/leads/${data.lead_id}`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setProcessing(p => ({ ...p, [inquiry.id]: false }));
    }
  };

  const unprocessed = inquiries.filter(i => !i.lead_id || i.status === "NEW");
  const processed = inquiries.filter(i => i.lead_id && i.status !== "NEW");

  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Inbox className="w-5 h-5 text-[#0176D3]" />
          <h1 className="text-xl font-bold text-[#181818] font-sans">Inquiry Inbox</h1>
          {unprocessed.length > 0 && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-400/10 border border-amber-400/20 text-[#8C4B00]">
              {unprocessed.length} new
            </span>
          )}
        </div>
        <button
          data-testid={INBOX.createInquiryBtn}
          onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-sm text-[#2E844A] hover:bg-emerald-500/20 transition-all font-sans">
          <PlusCircle className="w-4 h-4" /> New Inquiry
        </button>
      </div>

      {/* Create form modal */}
      {showCreateForm && <CreateInquiryModal onClose={() => setShowCreateForm(false)} onCreated={load} />}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#0176D3]" />
        </div>
      ) : (
        <>
          {/* Unprocessed */}
          {unprocessed.length > 0 && (
            <Section title="Pending Processing" accent="amber" count={unprocessed.length}>
              {unprocessed.map(inq => (
                <InquiryCard key={inq.id} inq={inq} processing={processing[inq.id]} onProcess={handleProcess} canProcess={canProcess} />
              ))}
            </Section>
          )}

          {/* Processed */}
          {processed.length > 0 && (
            <Section title="Processed" accent="slate" count={processed.length} collapsed>
              {processed.map(inq => (
                <InquiryCard key={inq.id} inq={inq} processing={processing[inq.id]} onProcess={handleProcess} canProcess={canProcess} />
              ))}
            </Section>
          )}

          {inquiries.length === 0 && (
            <div className="py-16 text-center bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl">
              <Inbox className="w-10 h-10 text-[#706E6B] mx-auto mb-3" />
              <p className="text-sm text-[#706E6B]">Inbox is empty</p>
              <p className="text-xs text-[#706E6B] mt-1">Create a manual inquiry or wait for web/email submissions</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Section({ title, accent, count, children, collapsed: initCollapsed = false }) {
  const [collapsed, setCollapsed] = useState(initCollapsed);
  return (
    <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl overflow-hidden">
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#EEF4FF] transition-colors">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#181818] font-sans">{title}</span>
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${accent === "amber" ? "text-[#8C4B00] bg-amber-400/10" : "text-[#706E6B] bg-slate-500/10"}`}>
            {count}
          </span>
        </div>
        <ChevronRight className={`w-4 h-4 text-[#706E6B] transition-transform ${collapsed ? "" : "rotate-90"}`} />
      </button>
      {!collapsed && <div className="divide-y divide-[#E5E5E5]/50" data-testid={INBOX.inquiryList}>{children}</div>}
    </div>
  );
}

function InquiryCard({ inq, processing, onProcess, canProcess }) {
  const CIcon = CHANNEL_ICON[inq.channel] || MessageSquare;
  const isProcessed = !!inq.lead_id;

  return (
    <div className="flex items-start gap-4 px-5 py-4 hover:bg-[#EEF4FF]/50 transition-colors" data-testid={INBOX.inquiryItem}>
      <div className={`w-8 h-8 rounded-lg bg-white border border-[#E5E5E5] flex items-center justify-center flex-shrink-0 mt-0.5`}>
        <CIcon className={`w-4 h-4 ${CHANNEL_COLOR[inq.channel] || "text-[#706E6B]"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-[#0176D3]">{inq.inquiry_number}</span>
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${STATUS_PILL[inq.status] || "text-[#706E6B] bg-slate-500/10 border-slate-500/20"}`}>
                {inq.status}
              </span>
              {inq.original_language && inq.original_language !== "en" && (
              <span className="text-[10px] font-mono text-[#5E4DB2] bg-[#F1ECFF] px-1.5 py-0.5 rounded">
                  {inq.original_language.toUpperCase()}
                </span>
              )}
            </div>
            <p className="text-sm text-[#181818] mt-0.5 font-sans">
              {inq.customer_name || "Unknown Customer"}
              {inq.customer_email && <span className="text-[#706E6B] text-xs ml-2">{inq.customer_email}</span>}
            </p>
            <p className="text-xs text-[#706E6B] mt-1 line-clamp-2 font-mono">{inq.raw_message || inq.subject || "—"}</p>
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <span className="text-[10px] text-[#706E6B]">{formatDate(inq.created_at)}</span>
            {isProcessed ? (
              <button
                data-testid={`${INBOX.processBtn}-view`}
                onClick={() => onProcess(inq)}
                className="flex items-center gap-1 text-xs text-[#2E844A] hover:text-[#2E844A] transition-colors">
                <CheckCircle2 className="w-3.5 h-3.5" /> View Lead
              </button>
            ) : canProcess ? (
              <button
                data-testid={INBOX.processBtn}
                onClick={() => onProcess(inq)}
                disabled={processing}
                className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-[#EEF4FF] border border-[#0176D3]/30 rounded text-[#0176D3] hover:bg-[#EEF4FF] disabled:opacity-60 disabled:cursor-not-allowed transition-all">
                {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                {processing ? "Processing..." : "Process with Agent"}
              </button>
            ) : (
              /* Unauthorized: show informative locked state, no clickable action */
              <div
                data-testid={`${INBOX.processBtn}-locked`}
                className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded bg-[#F3F3F3] border border-[#D8D8D8] text-[#706E6B] cursor-default select-none"
                title="Processing requires CRM lead creation authority. Contact your CRM Manager to process this inquiry."
              >
                <Lock className="w-3 h-3" />
                Requires delegation
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateInquiryModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    event_type_interest: "", notes: "", source_detail: "manual",
  });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name required"); return; }
    setLoading(true);
    try {
      await inquiryApi.createManual(form);
      toast.success("Inquiry created");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#F3F3F3]/80 ">
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl w-full max-w-md mx-4 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-[#181818] font-sans">New Manual Inquiry</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#444444] text-xl">×</button>
        </div>
        <div className="space-y-3">
          {[
            { key: "customer_name", label: "Customer Name *", ph: "Ananya Sharma" },
            { key: "customer_email", label: "Email", ph: "email@example.com" },
            { key: "customer_phone", label: "Phone", ph: "+91-98765..." },
            { key: "event_type_interest", label: "Event Type", ph: "Wedding, Engagement..." },
            { key: "source_detail", label: "Source", ph: "walk-in, referral, call..." },
          ].map(({ key, label, ph }) => (
            <div key={key}>
              <label className="text-xs text-[#706E6B] mb-1 block">{label}</label>
              <input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                placeholder={ph}
                className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#181818] placeholder-[#C9C9C9] focus:outline-none focus:border-[#0176D3]/30" />
            </div>
          ))}
          <div>
            <label className="text-xs text-[#706E6B] mb-1 block">Notes / Message</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Customer's requirements, notes..."
              rows={3}
              className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#181818] placeholder-[#C9C9C9] focus:outline-none focus:border-[#0176D3]/30 resize-none" />
          </div>
        </div>
        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-[#E5E5E5] rounded-lg text-sm text-[#706E6B] hover:text-[#181818] hover:border-slate-500 transition-colors">Cancel</button>
          <button onClick={submit} disabled={loading}
            className="flex-1 px-4 py-2 bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-sm text-[#0176D3] hover:bg-[#EEF4FF] disabled:opacity-60 transition-all flex items-center justify-center gap-2">
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {loading ? "Creating..." : "Create Inquiry"}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}
